import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SpaceWrapper } from "../../components";
import { getStateLabel } from "../../utils/Data";
import { useSelector, useDispatch } from "react-redux";
import {
  selectAllPickupAddresses,
  fetchPickupAddresses,
} from "../../app/Slices/pickupAddressSlice";

const DetailCard = ({ title, details }) => (
  <div className="bg-background_2 border border-secondary/5 p-4 rounded-lg shadow-sm hover:shadow transition-all duration-300">
    <h3 className="text-sm font-nunito font-bold text-secondary/90 mb-3 flex items-center gap-2">
      <div className="h-5 w-1 bg-primary rounded-full"></div>
      {title}
    </h3>
    <div className="space-y-1.5 ">
      {details.map(([label, value], index) => (
        <div
          key={index}
          className="flex items-center justify-between gap-4 text-sm py-1"
        >
          <span className="text-secondary/60">{label}</span>
          <span className="text-secondary/95 font-semibold font-sans text-end !leading-4">
            {value || "N/A"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FetchOrderDetails = () => {
  const dispatch = useDispatch();
  const courier = useSelector((state) => state.app?.appData?.courier) || [];
  const pickupAddresses = useSelector(selectAllPickupAddresses);
  const navigate = useNavigate();
  const { orderData } = useLocation().state || {};
  const [order, setOrder] = useState(orderData);

  const getCourierName = useCallback(
    (courierId) => {
      const courierInfo = courier.find((c) => c.id === courierId);
      return courierInfo?.name || "N/A";
    },
    [courier]
  );
  console.log("Order:", order);
  console.log("Pickup Addresses:", pickupAddresses);
  console.log("Order pickup address ID:", order?.pickupAddressId);

  const pickupAddress = pickupAddresses.find(
    (address) =>
      address._id === order?.pickupAddressId ||
      address.id === order?.pickupAddressId
  );
  console.log("Found pickup address:", pickupAddress);
  // Fetch pickup addresses when component mounts
  useEffect(() => {
    // If no order data is present (like after refresh), redirect to tracking
    if (!order) {
      navigate("/tracking");
      return;
    }

    // Fetch pickup addresses if they're not already loaded and we have an order with a user ID
    if (pickupAddresses.length === 0 && order?.order?.userId) {
      dispatch(fetchPickupAddresses({ userId: order.order.userId }));
    }
  }, [order, navigate, dispatch, pickupAddresses.length]);

  // If no data, return null (component will redirect)
  if (!order) return null;
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const orderDetails = [
    {
      title: "Order",
      details: [
        ["Order No", order?.order?.orderId],
        [
          "Date",
          order?.order?.orderDate ? formatDate(order.order?.orderDate) : "N/A",
        ],
        ["Payment Type", order?.order?.paymentMode],
        ["Order Weight", order?.order?.physicalWeight],
        [
          "Dimension",
          order?.order?.dimensions
            ? `${order?.order?.dimensions.length} × ${order?.order?.dimensions.breadth} × ${order?.order?.dimensions.height}`
            : "N/A",
        ],
      ],
    },
    {
      title: "Pickup Address",
      details: [
        ["Name", pickupAddress?.contactPerson || "N/A"],
        ["Contact", pickupAddress?.contactPhone || "N/A"],
        ["Address", pickupAddress?.fullAddress || "N/A"],
        ["City", pickupAddress?.city || "N/A"],
        ["Pincode", pickupAddress?.pincode || "N/A"],
      ],
    },
    {
      title: "Delivery Address",
      details: [
        ["Name", order?.deliveryAddress?.customerName],
        ["Contact", order?.deliveryPhones?.[0]?.phoneNumber],
        [
          "Address",
          [order?.deliveryAddress?.lines?.[0]?.line1, order?.deliveryAddress?.lines?.[0]?.line2]
            .filter(Boolean)
            .join(", "),
        ],
        ["Location", getStateLabel(order?.deliveryAddress?.state)],
      ],
    },
    {
      title: "Shipment Details",
      details: [
        ["Shipment Id", order?.shipmentId],
        [
          "Shipment Date",
          order?.shipmentDate ? formatDate(order.shipmentDate) : "N/A",
        ],
        ["Service Mode", order?.serviceMode],
        ["AWB Number", order?.awbNumber],
      ],
    },
    {
      title: "Delivery Details",
      details: [
        ["Status", order?.status],
        ["Zone", order?.zone],
        ["Courier", getCourierName(order?.courierId)],
        ["Pincode", getStateLabel(order?.deliveryAddress?.pincode)],
      ],
    },
  ];

  return (
    <SpaceWrapper>
      <div className="w-full max-w-6xl mx-auto p-3 space-y-4">
        {/* Header */}
        <div className="bg-background_2/95 border-b border-secondary/5 rounded-lg shadow-sm p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-headline4 font-nunito font-bold text-secondary">
                Order Details
              </h2>
              <p className="text-sm text-secondary/70">
                #{order?.order?.orderId}
              </p>
            </div>
            <span className="px-3 py-1 text-xs rounded-full bg-primary/90 text-secondary/70 font-nunito font-semibold border border-secondary/10">
              {order?.status || "Booked"}
            </span>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {orderDetails.map((section, index) => (
            <DetailCard key={index} {...section} />
          ))}
        </div>

        {/* Products Table */}
        <div className="bg-background_2 border border-secondary/5 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold font-nunito text-secondary/90 mb-3 flex items-center gap-2">
            <div className="h-5 w-1 bg-primary rounded-full"></div>
            Product Details
          </h3>
          <div className="overflow-x-auto border border-secondary/10 rounded-xl">
            <table className="min-w-full text-sm !rounded-lg overflow-hidden">
              <thead>
                <tr className="border-b border-secondary/10 bg-secondary/10">
                  {["Product", "Qty", "SKU", "HSN", "Total"].map((header) => (
                    <th
                      key={header}
                      className="text-left py-2 px-3 text-secondary/90  font-bold font-nunito"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {order?.order?.products?.map((product, index) => (
                  <tr
                    key={index}
                    className="border-b border-secondary/5 hover:bg-secondary/5"
                  >
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.productName || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.quantity || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.sku || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.hsn || "--"}
                    </td>
                    <td className="py-2 px-3 text-secondary/80">
                      {product?.orderValue || "--"}
                    </td>
                  </tr>
                ))}
                <tr className="font-medium bg-secondary/10">
                  <td
                    colSpan="4"
                    className="py-2 px-3 text-right text-secondary/90"
                  >
                    Total
                  </td>
                  <td className="py-2 px-3 text-secondary/90 font-bold">
                    {order?.order?.totalOrderValue} Rs.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SpaceWrapper>
  );
};

export default FetchOrderDetails;
